import {ReactNode} from 'react';
import Box from '@mui/material/Box';
import {SxProps, Theme} from '@mui/material/styles';
import Card from './Card';
import {kit, radius} from './tokens';

export interface FactRow {
  icon?: ReactNode;
  // Omit for a plain icon+value row (e.g. an address line) with no leading
  // label -- the value then sits left-aligned right after the icon instead
  // of being pushed to the row's right edge.
  label?: string;
  value: ReactNode;
}

interface RailFactsCardProps {
  title: string;
  rows: FactRow[];
  // Optional map (or other visual) slot -- the container reserves the
  // approved 190px-tall bordered tile; the caller supplies real content
  // (this kit never fakes a map).
  mapSlot?: ReactNode;
  // Optional link/button under the rows (and map, if present), e.g.
  // "Edit service area".
  footer?: ReactNode;
  sx?: SxProps<Theme>;
}

// Right-rail "facts" card: a labeled list of icon+label+value rows with
// hairline dividers, an optional map slot, and an optional footer link. Web
// port of `.card.facts` in web-detail-1-quotes.html.
//
// 768-1023 (tablet): rows and the map go side by side ("facts goes wide"
// per the approved rulebook) instead of stacking; that band folds back to
// the stacked layout again at 1024+ once the two-column page returns.
const RailFactsCard = ({
  title,
  rows,
  mapSlot,
  footer,
  sx,
}: RailFactsCardProps) => (
  <Card
    radius="lg"
    sx={[{padding: '18px 20px 20px'}, ...(Array.isArray(sx) ? sx : [sx])]}>
    <Box
      sx={(theme) => ({
        [theme.breakpoints.between(768, 1024)]: mapSlot
          ? {display: 'flex', gap: '26px', alignItems: 'stretch'}
          : undefined,
      })}>
      <Box sx={{flex: 1, minWidth: 0}}>
        <Box
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: kit.slate,
            marginBottom: '6px',
          }}>
          {title}
        </Box>
        {rows.map((row, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '13px 0',
              borderTop: index === 0 ? 'none' : `1px solid ${kit.mist}`,
              fontSize: 14,
              fontWeight: 600,
              color: kit.ink,
            }}>
            {row.icon ? (
              <Box
                sx={{
                  color: kit.blueInk,
                  display: 'flex',
                  flexShrink: 0,
                }}>
                {row.icon}
              </Box>
            ) : null}
            {row.label ? (
              <Box
                component="span"
                sx={{flex: 1, color: kit.slate, fontWeight: 500}}>
                {row.label}
              </Box>
            ) : null}
            {row.value}
          </Box>
        ))}
      </Box>
      {mapSlot ? (
        <Box
          sx={(theme) => ({
            marginTop: '6px',
            height: 190,
            borderRadius: `${radius.md}px`,
            border: `1px solid ${kit.line}`,
            overflow: 'hidden',
            position: 'relative',
            [theme.breakpoints.between(768, 1024)]: {
              marginTop: 0,
              width: 308,
              height: 'auto',
              minHeight: 196,
              flexShrink: 0,
            },
          })}>
          {mapSlot}
        </Box>
      ) : null}
    </Box>
    {footer ? <Box sx={{marginTop: '10px'}}>{footer}</Box> : null}
  </Card>
);

export default RailFactsCard;
